import { useEffect, useState, useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import HeadingGroup from "../../common/components/headingGroup/HeadingGroup";
import InvoiceFilters from "./components/InvoiceFilters";
import InvoiceTable from "./components/InvoiceTable";
import { getFacturas } from "../../services/compra.services";
import { useFiltersStore } from "@/common/zustand/filters.store.js";
import { useTablaStore } from "@/common/zustand/tabla.store.js";
import { useComprasAuth } from "../../common/hooks/useComprasAuth";
import { useDropdownsStore } from "@/common/zustand/dropdowns.store.js";
import { fetchSectoresDropdown } from "./services/modalFilter.services.js";
import * as sentry from "@sentry/browser";

export default function Inicio() {
    const [data, setData] = useState([]);
    const [headers, setHeaders] = useState({});
    const [loading, setLoading] = useState(false);
    const [excelData, setExcelData] = useState([]);
    const traceIdRef = useRef(uuidv4());
    const abortRef = useRef(null);
    const appliedFilters = useFiltersStore((s) => s.appliedFilters);
    const setAppliedFiltersStore = useFiltersStore((s) => s.setAppliedFilters);
    const searchTerm = useFiltersStore((s) => s.searchTerm);
    const setSearchTermStore = useFiltersStore((s) => s.setSearchTerm);
    const setDefaultsEnabled = useFiltersStore((s) => s.setDefaultsEnabled);
    const { username, userDetails } = useComprasAuth();
    const getDefaultFilters = useFiltersStore((state) => state.getDefaultFilters);
    const clearPersisted = useFiltersStore((s) => s.clearPersisted);
    
    // Store de tabla para sortConfig persistente
    const sortConfig = useTablaStore((s) => s.sortConfig);
    const setSortConfigStore = useTablaStore((s) => s.setSortConfig);

    // Store de dropdowns para sectores
    const setSectores = useDropdownsStore((s) => s.setSectores);
    const needsReloadSectores = useDropdownsStore((s) => s.needsReloadSectores);

    const lastPaginationRef = useRef({ page: undefined, pageSize: undefined, offset: undefined });
    
    const sortConfigRef = useRef(sortConfig);
    useEffect(() => {
        sortConfigRef.current = sortConfig;
    }, [sortConfig]);

    const buildSortParam = (sort) => {
        if (!sort?.key || !sort?.direction) return null;
        const sortBy = sort.direction === 'ascending' ? 'ASC' : sort.direction === 'descending' ? 'DESC' : null;
        return sortBy ? [{ key: sort.key, sortBy }] : null;
    };

    const fetchApi = useCallback(async (
        filter = {},
        page = 1,
        pageSize = 10,
        forceFirstPage = false,
        nuevoTraceId = true,
        showSpinner = true,
        isClear = false,
        currentSort = null
    ) => {
        const todosNulos = Object.values(filter).every(
            (value) => value == null || value === ""
        );
        const normalizedSearch = String(filter.search ?? "").trim();

        if (todosNulos && normalizedSearch === "") {
            if (isClear) {
                setAppliedFiltersStore({});
                traceIdRef.current = uuidv4();
                setExcelData([]);
                setDefaultsEnabled(false);
                clearPersisted();
            }
        }

        try {
            if (abortRef.current) {
                abortRef.current.abort();
            }
            const controller = new AbortController();
            abortRef.current = controller;
            if (showSpinner) {
                setData([]);
                setLoading(true);
            }

            const traceIdActual = nuevoTraceId ? uuidv4() : traceIdRef.current;
            if (nuevoTraceId) traceIdRef.current = traceIdActual;

            const effectivePageSize = pageSize || 10;
            const effectiveForceFirstPage = forceFirstPage || nuevoTraceId;
            const actualPage = effectiveForceFirstPage ? 1 : (page || 1);
            const offset = Math.max(0, (actualPage - 1) * effectivePageSize);

            lastPaginationRef.current = { page: actualPage, pageSize: effectivePageSize, offset };

            const params = {
                ...filter,
                offset,
                limit: effectivePageSize,
                traceId: traceIdActual,
                ...(username && { usuario: username }), 
            };

            const sort = buildSortParam(currentSort || sortConfig);
            if (sort) {
                params.sort = JSON.stringify(sort);
            }

            const response = await getFacturas(params, { signal: controller.signal });

            if (response && response.facturas && Array.isArray(response.facturas)) {
                setData(response.facturas);
                setHeaders(response.headers || {});
                
                setLoading(false);

                const totalCount = response.headers?.["X-Total-Count"] || response.headers?.["x-total-count"];

                const paramsExcel = {
                    ...filter, 
                    offset: 0,
                    limit: totalCount || 999999,
                    traceId: traceIdActual,
                    ...(username && { usuario: username }),
                };
                
                getFacturas(paramsExcel, { signal: controller.signal })
                    .then(responseExcel => {
                        if (responseExcel && responseExcel.facturas && Array.isArray(responseExcel.facturas)) {
                            setExcelData(responseExcel.facturas);
                        } else {
                            setExcelData([]);
                        }
                    })
                    .catch(() => {
                        setExcelData([]);
                    });
            } else {
                setData([]);
                setHeaders({});
                setExcelData([]);
                setLoading(false);
            }
        } catch (error) {
            if (error?.code === 'ERR_CANCELED') {
                return;
            }
            sentry.captureException(error);
            setData([]);
            setHeaders({});
            setExcelData([]);
            setLoading(false);
        }
    }, [setAppliedFiltersStore, clearPersisted, setDefaultsEnabled, username]);

    const handleSearch = (searchValue) => {
        setSearchTermStore(searchValue);

        const filter = {
            ...appliedFilters,
            ...(searchValue ? { search: searchValue } : {}),
        };
        if (
            (!searchValue || String(searchValue).trim() === '') &&
            (!appliedFilters || Object.keys(appliedFilters).length === 0)
        ) {
            setDefaultsEnabled(false);
        }
        const isClear =
            (!searchValue || String(searchValue).trim() === '') &&
            (!appliedFilters || Object.keys(appliedFilters).length === 0);
        fetchApi(filter, 1, 10, true, true, true, isClear, sortConfigRef.current);
    };

    const handlePageChange = useCallback(({ page: newPage, pageSize: newPageSize, offset, pageIndex, currentPage, itemsPerPage, size, control }) => {
        if (loading) return;

        if (control === 'clamp') {
            return;
        }

        const resolvedPage = (newPage ?? currentPage ?? (typeof pageIndex === 'number' ? pageIndex + 1 : undefined) ?? 1);
        const resolvedPageSize = (newPageSize ?? itemsPerPage ?? size ?? 10);
        const resolvedOffset = typeof offset === 'number'
            ? offset
            : Math.max(0, (resolvedPage - 1) * resolvedPageSize);

        const prev = lastPaginationRef.current;
        if (control === 'size' && prev && resolvedPageSize === prev.pageSize) {
            return;
        }

        const samePage = prev.page === resolvedPage && prev.pageSize === resolvedPageSize && prev.offset === resolvedOffset;
        if (samePage) {
            return;
        }

        lastPaginationRef.current = { page: resolvedPage, pageSize: resolvedPageSize, offset: resolvedOffset };

        const filter = {
            ...appliedFilters,
            ...(searchTerm ? { search: searchTerm } : {})
        };

        fetchApi(filter, resolvedPage, resolvedPageSize, false, false, false, false, sortConfigRef.current);
    }, [appliedFilters, searchTerm, fetchApi, loading]);

    const handleSortChange = (newSortConfig) => {
        setSortConfigStore(newSortConfig);
        const filter = {
            ...appliedFilters,
            ...(searchTerm ? { search: searchTerm } : {})
        };
        const currentPage = lastPaginationRef.current.page || 1;
        const currentPageSize = lastPaginationRef.current.pageSize || 10;
        fetchApi(filter, currentPage, currentPageSize, false, false, true, false, newSortConfig);
    };

    const handleFiltersApply = (filters) => {
        setDefaultsEnabled(true);
        setAppliedFiltersStore(filters);
        const combinedFilters = {
            ...filters,
            ...(searchTerm ? { search: searchTerm } : {})
        };
        fetchApi(combinedFilters, 1, 10, true, true, true, false, sortConfigRef.current);
    };

    const handleRemoveFilter = (filterKey) => {
       
        if (filterKey === 'search') {
            setSearchTermStore("");
        }
        const updatedFilters = { ...appliedFilters };
        delete updatedFilters[filterKey];
        setAppliedFiltersStore(updatedFilters);

        const combinedFilters = {
            ...updatedFilters,
            ...(filterKey === 'search' ? {} : (searchTerm ? { search: searchTerm } : {}))
        };
        if (Object.keys(updatedFilters).length === 0 && (!searchTerm || String(searchTerm).trim() === "")) {
            setDefaultsEnabled(false);
        }
        const isClear = Object.keys(updatedFilters).length === 0 && (!searchTerm || String(searchTerm).trim() === "");
        fetchApi(combinedFilters, 1, 10, true, true, true, isClear, sortConfigRef.current);
    };

    // Cargar sectores al inicio si hay filtros de sector_destino persistidos
    useEffect(() => {
        const centroCosto = userDetails?.CENTRO_COSTO || null;
        
        if (!username) return;
        
        // Verificar si hay filtros de sector_destino en appliedFilters
        const hasSectorFilter = appliedFilters?.sector_destino &&
                                (Array.isArray(appliedFilters.sector_destino) ?
                                 appliedFilters.sector_destino.length > 0 :
                                 Boolean(appliedFilters.sector_destino));
        
        // Solo cargar si hay filtros de sector o si necesita recarga
        if (hasSectorFilter || needsReloadSectores(username, centroCosto)) {
            const loadSectores = async () => {
                try {
                    const sectoresData = await fetchSectoresDropdown(username, centroCosto);
                    setSectores(sectoresData, username, centroCosto);
                } catch (error) {
                    console.error('Error cargando sectores en inicio:', error);
                }
            };
            
            loadSectores();
        }
    }, [username, userDetails, appliedFilters, setSectores, needsReloadSectores]);

    useEffect(() => {
        const wasFiltersVisible = sessionStorage.getItem('compras_filters_visible');
        const wasTablaVisible = sessionStorage.getItem('compras_tabla_visible');
        
        // Si NO existe la bandera, significa que cerraron la pestaña → resetear
        if (!wasFiltersVisible) {
            const store = useFiltersStore.getState();
            const defaultFilters = store.getDefaultFilters();
            store.setAppliedFilters(defaultFilters);
            store.setSearchTerm("");
            store.setDefaultsEnabled(true);
            
            // Establecer las banderas como activas
            sessionStorage.setItem('compras_filters_visible', 'true');
            sessionStorage.setItem('compras_tabla_visible', 'true');
            
            // Resetear sort también
            if (!wasTablaVisible) {
                useTablaStore.getState().resetSort();
            }
            
            // Aplicar filtro Pendiente
            fetchApi(defaultFilters, 1, 10, true, true, true, false, sortConfigRef.current);
            return;
        }
        
        // Si existe la bandera, fue una recarga → mantener filtros y sort actuales
        sessionStorage.setItem('compras_filters_visible', 'true');
        sessionStorage.setItem('compras_tabla_visible', 'true');
        
        const st = useFiltersStore.getState();
        const af = st.appliedFilters;
        const s = st.searchTerm;
        const defaultsEnabled = st.defaultsEnabled;
        const hasFilters = af && Object.keys(af).length > 0;
        const hasSearch = Boolean(s && s.trim() !== "");
        if (hasFilters || hasSearch) {
            const combined = { ...(af || {}), ...(hasSearch ? { search: s } : {}) };
            fetchApi(combined, 1, 10, true, true, true, false, sortConfigRef.current);
        } else {
            if (defaultsEnabled) {
                const defaultFilters = getDefaultFilters();
                st.setAppliedFilters(defaultFilters);
                fetchApi(defaultFilters, 1, 10, true, true, true, false, sortConfigRef.current);
            } else {
                fetchApi({}, 1, 10, true, true, true, false, sortConfigRef.current);
            }
        }
    }, [getDefaultFilters, fetchApi]);

    return (
        <div>
            <HeadingGroup
                title="Revisión de facturas"
                description="Visualizá todas las facturas asignadas a tu sector, clasificadas por estado: pendientes, aprobadas y rechazadas."
                className="mt-4 mb-6"
            />
            <InvoiceFilters 
                onSearch={handleSearch} 
                onFetchExcelData={() => excelData} 
                onFiltersApply={handleFiltersApply}
                appliedFilters={appliedFilters}
                onRemoveFilter={handleRemoveFilter}
            />
            <InvoiceTable 
                data={data} 
                headers={headers} 
                onPageChange={handlePageChange} 
                loading={loading} 
                hasSearch={Boolean(searchTerm && searchTerm.trim() !== "")} 
                sortConfig={sortConfig}
                setSortConfig={handleSortChange}
            />
        </div>
    )
}